/**
 * MASUNG printer commands. Every byte matches PrintCmd.java exactly.
 * No duplicates — one export per function.
 */

// ── 3.1 Command Mode ──
export function cmdSetCommandMode(mode = 3) {
  return new Uint8Array([0x1B, 0x79, mode === 2 || mode === 3 ? mode : 3]);
}

// ── 3.2 Init (ESC @) ──
export const CMD_INIT = new Uint8Array([0x1B, 0x40]);

// ── 3.3 Line Spacing (ESC 3) ──
export function cmdSetLineSpacing(n: number) {
  return new Uint8Array([0x1B, 0x33, Math.min(n, 127)]);
}

// ── 3.4 Char Spacing (ESC SP) ──
export function cmdSetCharSpacing(n: number) {
  return new Uint8Array([0x1B, 0x20, Math.min(n, 64)]);
}

// ── 3.5 Chinese Spacing (FS S) ──
export function cmdSetChineseSpacing(l: number, r: number) {
  return new Uint8Array([0x1C, 0x53, Math.min(l, 64), Math.min(r, 64)]);
}

// ── 3.6 Left Margin (GS L) ──
/**
 * The head is 832 dots — 104mm at 8 dots/mm, which is the MS-TS102's stated print width.
 *
 * This used to read `if (n > 576) n = 0`, which is not a clamp but an INVERSION: ask for a
 * margin past 72mm and the print jumps to the far LEFT edge instead. 576 dots is the width of
 * an 80mm-class printer, so the constant was right for a machine this isn't, and everything
 * from 72mm to 104mm was silently unreachable.
 *
 * Out-of-range now pins to the edge, which is wrong in a way you can SEE, rather than wrong in
 * a way that looks deliberate.
 */
export const HEAD_DOTS = 832;
export function cmdSetLeftMargin(n: number) {
  const margin = Math.max(0, Math.min(Math.round(n), HEAD_DOTS));
  return new Uint8Array([0x1D, 0x4C, margin % 256, Math.floor(margin / 256)]);
}

// ── Print Width (GS W) ──
export function cmdSetPrintWidth(n: number) {
  return new Uint8Array([0x1D, 0x57, n % 256, Math.floor(n / 256)]);
}

// ── 3.23 Right Margin (ESC SP) ──
export function cmdSetRightMargin(n: number) {
  return new Uint8Array([0x1B, 0x20, n & 0xFF]);
}

// ── 3.7-3.8 Blackmark offsets (vendor) ──
export function cmdSetMarkCutOffset(o: number) {
  o = Math.min(o, 1600);
  return new Uint8Array([0x13, 0x74, 0x33, 0x78, (o >> 8) & 0xFF, o & 0xFF]);
}
export function cmdSetMarkPrintOffset(o: number) {
  o = Math.min(o, 1600);
  return new Uint8Array([0x13, 0x74, 0x11, 0x78, (o >> 8) & 0xFF, o & 0xFF]);
}

// ── 3.9 Chinese Size (FS !) ──
export function cmdSetChineseSize(h: number, w: number, u: number, f: number) {
  return new Uint8Array([0x1C, 0x21, Math.min(h,1)*8 + Math.min(w,1)*4 + Math.min(u,1)*128 + (f&1)]);
}

// ── 3.10 ASCII Char Size (ESC !) ──
export function cmdSetCharSize(h: number, w: number, u: number, t: number) {
  return new Uint8Array([0x1B, 0x21, Math.min(h,1)*16 + Math.min(w,1)*32 + Math.min(u,1)*128 + Math.min(t,1)]);
}

// ── 3.11 Text Magnify (GS !) ──
export function cmdSetTextSize(w: number, h: number) {
  return new Uint8Array([0x1D, 0x21, ((Math.min(Math.max(w,1),8)-1)*16 + (Math.min(Math.max(h,1),8)-1)) & 0xFF]);
}

// ── 3.12 Alignment (ESC a) ──
export function cmdSetAlignment(n: number) {
  return new Uint8Array([0x1B, 0x61, Math.min(n, 2)]);
}

// ── 3.13 Bold (ESC G) ──
export function cmdSetBold(on: boolean) {
  return new Uint8Array([0x1B, 0x47, on ? 1 : 0]);
}

// ── 3.14 Rotation 90 (ESC V) ──
export function cmdSetRotation(on: boolean) {
  return new Uint8Array([0x1B, 0x56, on ? 1 : 0]);
}

// ── 3.15 Direction 180 (ESC {) ──
export function cmdSetDirection(n: number) {
  return new Uint8Array([0x1B, 0x7B, n === 1 ? 1 : 0]);
}

// ── 3.16 Inverse (GS B) ──
export function cmdSetInverse(on: boolean) {
  return new Uint8Array([0x1D, 0x42, on ? 1 : 0]);
}

// ── 3.17 Italic (ESC % G/H) ──
export function cmdSetItalic(on: boolean) {
  return new Uint8Array([0x1B, 0x25, on ? 0x47 : 0x48]);
}

// ── 3.18 Underline (ESC -) ──
export function cmdSetUnderline(n: number) {
  return new Uint8Array([0x1B, 0x2D, Math.min(n, 2)]);
}

// ── 3.19 Chinese Mode (FS & / FS .) ──
export function cmdEnterChineseMode() { return new Uint8Array([0x1C, 0x26]); }
export function cmdExitChineseMode() { return new Uint8Array([0x1C, 0x2E]); }

// ── 3.20 Tab Positions (ESC D) ──
export function cmdSetTabPositions(pos: number[]) {
  const b = [0x1B, 0x44]; pos.slice(0, 32).forEach(p => b.push(p)); b.push(0);
  return new Uint8Array(b);
}
export const CMD_TAB = new Uint8Array([0x09]);

// ── 3.21 Codepage (ESC R + ESC t) ──
export function cmdSetCodepage(country: number, cp: number) {
  return new Uint8Array([0x1B, 0x52, country < 11 ? country : 0, 0x1B, 0x74, cp < 27 ? cp : 0]);
}

// ── 3.22 Self Test (GS ( A) ──
export function cmdSelfTest() {
  return new Uint8Array([0x1D, 0x28, 0x41, 0x02, 0x00, 0x00, 0x02]);
}

// ── 3.24 Barcode Alignment (GS P) ──
export function cmdSetBarcodeAlignment(n: number) {
  return new Uint8Array([0x1D, 0x50, n]);
}

// ── 3.25 Feed Lines (ESC d) ──
export function cmdFeedLines(n: number) { return new Uint8Array([0x1B, 0x64, n & 0xFF]); }

// ── Print text + optional LF ──
export function cmdPrintText(text: string, newline = true) {
  const enc = new TextEncoder().encode(text);
  if (!newline) return enc;
  const buf = new Uint8Array(enc.length + 1); buf.set(enc); buf[enc.length] = 0x0A; return buf;
}

// ── 3.26 Blank Line (LF) ──
export const CMD_LF = new Uint8Array([0x0A]);

// ── 3.28 Feed Dots (ESC J) ──
/**
 * ONE ESC J CARRIES A SINGLE BYTE, so 255 dots (31.9mm) is the protocol's ceiling, not a choice.
 *
 * This used to `Math.min(n, 250)`. That is the difference between a limit and a LIE: asking for
 * a 960-dot feed (a 120mm ticket pitch) returned a command that fed 250 and reported nothing,
 * so the paper stopped a third of the way and every caller above believed it had fed a pitch.
 *
 * The clamp stays — a byte cannot hold more — but it is no longer where the decision is made.
 * `MasungPrinter.feedDots` splits a long feed into as many ESC J's as it takes, so callers can
 * ask for a real distance and get it. Anything that wants one command's worth still gets an
 * honest one.
 */
export const MAX_FEED_DOTS_PER_CMD = 255;
export function cmdFeedDots(n: number) {
  const dots = Math.max(0, Math.min(Math.round(n), MAX_FEED_DOTS_PER_CMD));
  return new Uint8Array([0x1B, 0x4A, dots]);
}

// ── 3.30 Cut (ESC i / ESC m) ──
export function cmdCut(full = true) { return new Uint8Array([0x1B, full ? 0x69 : 0x6D]); }

// ── 3.31-3.35 Black Mark ──
export const CMD_MARK_POS = new Uint8Array([0x0C]);
export const CMD_MARK_FEED_PRINT = new Uint8Array([0x1B, 0x0C]);
export const CMD_MARK_FEED_CUT = new Uint8Array([0x1D, 0x0C]);
export function cmdMarkCut(detect = true) { return new Uint8Array([0x1D, 0x56, 0x42, detect ? 0 : 1]); }

// ── 3.36 QR Code (vendor 0x13 0x50 0x48) ──
export function cmdQrcode(data: string, moduleSize = 5) {
  const d = new TextEncoder().encode(data); const len = d.length;
  const buf = new Uint8Array(26 + len); let i = 0;
  buf[i++]=0x13;buf[i++]=0x50;buf[i++]=0x48;buf[i++]=0x01;buf[i++]=0x01;
  buf[i++]=0x13;buf[i++]=0x50;buf[i++]=0x48;buf[i++]=0x02;buf[i++]=0x01;
  buf[i++]=0x13;buf[i++]=0x50;buf[i++]=0x48;buf[i++]=0x03;buf[i++]=Math.min(Math.max(moduleSize,1),16);
  buf[i++]=0x13;buf[i++]=0x50;buf[i++]=0x48;buf[i++]=0x04;buf[i++]=0x01;
  buf[i++]=Math.floor(len/256);buf[i++]=len%256;
  buf.set(d, i); i += len;
  buf[i++]=0x13;buf[i++]=0x50;buf[i++]=0x48;buf[i++]=0x05;
  return buf.slice(0, i);
}

export const CMD_PRINT_REMAIN_QR = new Uint8Array([0x13, 0x0C]);

// ── 3.36 PDF417 ──
export function cmdPdf417(dw: number, dh: number, rows: number, cols: number, data: string) {
  const w = (dw < 2 || dw > 6) ? 2 : dw;
  const d = new TextEncoder().encode(data); const len = d.length;
  const buf = new Uint8Array(128); let i = 0;
  buf[i++]=0x1D;buf[i++]=0x77;buf[i++]=w;
  buf[i++]=0x1D;buf[i++]=0x68;buf[i++]=dh&0xFF;
  buf[i++]=0x1D;buf[i++]=0x6B;buf[i++]=0x4C;
  buf[i++]=rows&0xFF;buf[i++]=cols&0xFF;buf[i++]=len&0xFF;
  buf.set(d, i); return buf.slice(0, i + len);
}

// ── 3.37 1D Barcode (matches SDK Print1Dbar exactly) ──
export function cmdBarcode1d(codeType: number, data: string, width = 3, height = 72, hriSize = 0, hriPos = 2) {
  const w = (width < 2 || width > 6) ? 2 : width;
  const h = (height < 24 || height > 250) ? 24 : height;
  const buf = new Uint8Array(256); let i = 0;
  buf[i++]=0x1D;buf[i++]=0x77;buf[i++]=w;
  buf[i++]=0x1D;buf[i++]=0x68;buf[i++]=h;
  buf[i++]=0x1D;buf[i++]=0x66;buf[i++]=hriSize>1?0:hriSize;
  buf[i++]=0x1D;buf[i++]=0x48;buf[i++]=hriPos>3?0:hriPos;
  buf[i++]=0x1D;buf[i++]=0x6B;
  const d = new TextEncoder().encode(data); const len = d.length;
  const dig = (b:number) => b >= 48 && b <= 57;
  switch (codeType) {
    case 0: buf[i++]=0;for(let j=0;j<Math.min(11,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 1: buf[i++]=1;for(let j=0;j<Math.min(11,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 2: buf[i++]=2;for(let j=0;j<Math.min(12,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 3: buf[i++]=3;for(let j=0;j<Math.min(7,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 4: buf[i++]=4;for(let j=0;j<len;j++)buf[i++]=d[j];buf[i++]=0;break;
    case 5: buf[i++]=5;{const e=len%2===0?len:len-1;for(let j=0;j<e;j++)buf[i++]=d[j];}buf[i++]=0;break;
    case 6: buf[i++]=6;for(let j=0;j<len;j++)buf[i++]=d[j];buf[i++]=0;break;
    case 7: buf[i++]=7;for(let j=0;j<Math.min(12,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 8: buf[i++]=8;for(let j=0;j<Math.min(7,len);j++)buf[i++]=d[j];buf[i++]=0;break;
    case 9: buf[i++]=72;buf[i++]=len&0xFF;for(let j=0;j<len;j++)buf[i++]=d[j];break;
    case 10: {
      buf[i++]=73;
      if(d.every(dig)){
        buf[i++]=len%2===1?Math.floor(len/2)+5:Math.floor(len/2)+2;
        buf[i++]=123;buf[i++]=67;let j=0;
        while(j<len){if(j+1>=len){buf[i++]=123;buf[i++]=66;buf[i++]=d[j];j++}else{buf[i++]=(d[j]-48)*10+(d[j+1]-48);j+=2}}
      }else{buf[i++]=len+2;buf[i++]=123;buf[i++]=66;for(let j=0;j<len;j++)buf[i++]=d[j];}
      break;
    }
    default: buf[i++]=Math.min(codeType,10);for(let j=0;j<len;j++)buf[i++]=d[j];buf[i++]=0;
  }
  return buf.slice(0, i);
}

// ── Print NV Bitmap (FS p) ──
/**
 * FS q — define NV bitmaps (vendor doc §5.2). Each image: width and height in dots, both multiples of
 * 8; data COLUMN-MAJOR, each column top to bottom in bytes, MSB at the top (k = x·y·8 bytes).
 * WRITES FLASH and REPLACES EVERY STORED IMAGE. Vendor: "less than 10 times". The printer is busy
 * while writing and may reset afterwards.
 */
export function cmdDefineNv(images: { widthDots: number; heightDots: number; columns: Uint8Array }[]) {
  const parts: Uint8Array[] = [new Uint8Array([0x1C, 0x71, images.length])];
  for (const im of images) {
    const x = im.widthDots / 8, y = im.heightDots / 8;
    if (!Number.isInteger(x) || !Number.isInteger(y) || x < 1 || x > 1023 || y < 1 || y > 288) throw new Error(`NV image ${im.widthDots}×${im.heightDots} is outside 8–8184 × 8–2304 dots in steps of 8`);
    if (im.columns.length !== x * y * 8) throw new Error('NV image data length does not match its size');
    parts.push(new Uint8Array([x & 0xFF, x >> 8, y & 0xFF, y >> 8]), im.columns);
  }
  return concat(...parts);
}

export function cmdPrintNvBmp(index: number, mode = 48) {
  return new Uint8Array([0x1C, 0x70, index, Math.min(Math.max(mode, 48), 51)]);
}

// ── Vendor Rotation Mode ──
export const CMD_ENTER_ROTATION = new Uint8Array([0x13, 0x44]);
export const CMD_PRINT_ROTATION = new Uint8Array([0x0B]);
export function cmdRotationLeftMargin(n: number) { return new Uint8Array([0x13, 0x76, n < 72 ? n : 0]); }
export function cmdSetPrinterId(name: string) {
  const d = new TextEncoder().encode(name.slice(0, 30));
  const b = new Uint8Array(3 + d.length); b[0]=0x13;b[1]=0x75;b[2]=d.length;b.set(d,3);return b;
}

// ── Status Queries (DLE EOT 1-5) ──
export const CMD_STATUS1 = new Uint8Array([0x10, 0x04, 0x01]);
export const CMD_STATUS2 = new Uint8Array([0x10, 0x04, 0x02]);
export const CMD_STATUS3 = new Uint8Array([0x10, 0x04, 0x03]);
export const CMD_STATUS4 = new Uint8Array([0x10, 0x04, 0x04]);
export const CMD_STATUS5 = new Uint8Array([0x10, 0x04, 0x05]);
export function cmdGetInfo(t: number) { return new Uint8Array([0x1D, 0x49, t]); }

// ── Status Parsers (match SDK CheckStatus1-5) ──
export function parseStatus1(b: number) { return (b & 0x16) !== 0x16 ? 2 : 0; }
export function parseStatus2(b: number) { return (b & 0x04) === 0x04 ? 3 : 0; }
export function parseStatus3(b: number) {
  if ((b & 0x08) === 0x08) return 4; if ((b & 0x40) === 0x40) return 5; if ((b & 0x20) === 0x20) return 6; return 0;
}
export function parseStatus4(b: number) {
  if ((b & 0x60) === 0x60) return 7; if ((b & 0x0C) === 0x0C) return 8; return 0;
}
export function parseStatus5(b: number) {
  if ((b & 0x80) === 0x80) return 4; if ((b & 0x01) === 0x01) return 5;
  if ((b & 0x08) === 0x08) return 6; if ((b & 0x02) === 0x02) return 7;
  if ((b & 0x04) === 0x04) return 8; return 0;
}

// ── Utility ──
export function concat(...parts: Uint8Array[]) {
  let total = 0; for (const p of parts) total += p.length;
  const buf = new Uint8Array(total); let off = 0;
  for (const p of parts) { buf.set(p, off); off += p.length; } return buf;
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// SETUP-WIZARD COMMANDS — sourced in ~/printer/KNOWLEDGE.md. None of these were in the SDK wrappers
// this file was copied from, which is why every earlier attempt concluded they could not be done.
// ════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * PAPER TYPE — the switch that actually puts the printer into black-mark mode.
 *
 * Not in any Masung SDK. Recovered from the vendor settings tool (MSPrinterTools V1.0.1.8, "Black
 * Mark/Label") and the MASUNG Print app's SetPaperTypeCmd. `ESC y 3`, which this codebase believed
 * enabled the sensor, only selects the command set. A printer left in continuous mode accepts
 * `ESC FF`/`GS FF` and does nothing — no motion, no error — and ignores both mark offsets, which is
 * how a cut offset tuned on one unit "stopped working" on the next.
 *
 * Stored in the printer and (as observed with the offsets) restarts it. Written once, at setup.
 */
export type PaperType = 'mark' | 'gap' | 'continuous';
export const PAPER_TYPE_BYTE: Record<PaperType, number> = { mark: 0x33, gap: 0x35, continuous: 0xCC };
export function cmdSetPaperType(t: PaperType) {
  return new Uint8Array([0x13, 0x74, 0x44, 0x33, PAPER_TYPE_BYTE[t]]);
}

/** Mark-sensor threshold, 32..200 (vendor default 48). Stored. */
export function cmdSetMarkThreshold(n: number) {
  return new Uint8Array([0x13, 0x74, 0x11, 0x66, Math.max(32, Math.min(200, Math.round(n)))]);
}

/** Raw mark/paper sensor AD reading — one byte back. Used to trace the stock and find its marks. */
export const CMD_READ_SENSOR = new Uint8Array([0x1B, 0xCC, 0x01]);

/** Masung printer-type query; reply `12 53 41 <type>`. */
export const CMD_PRINTER_TYPE = new Uint8Array([0x12, 0x53, 0x41]);

/** GS r 1 — paper sensor status byte. */
export const CMD_PAPER_SENSOR_STATUS = new Uint8Array([0x1D, 0x72, 0x01]);

/** DLE ENQ — recover from a black-mark / cutter error. 1 = resume, 2 = clear buffer and resume. */
export function cmdRecover(clearBuffer = false) { return new Uint8Array([0x10, 0x05, clearBuffer ? 0x02 : 0x01]); }

/** FS . — leave the GBK/Chinese mode the printer boots in, so Latin text is not read as double-byte. */
export const CMD_CANCEL_CHINESE = new Uint8Array([0x1C, 0x2E]);

/** ESC K n — reverse feed n dots (≤255). The vendor app pulls back 90 dots after a cut. */
export function cmdBackFeedDots(n: number) {
  return new Uint8Array([0x1B, 0x4B, Math.max(0, Math.min(Math.round(n), 255))]);
}

/**
 * QR via standard ESC/POS `GS ( k` (model 2). This is the form "IP1000 Printer" firmware prints;
 * "Masung Printer" firmware prints it as literal text. Which one a unit is, is PROBED at setup —
 * see `QrMethod` — never assumed.
 */
export function cmdQrcodeEscPos(data: string, moduleSize = 6, ecc: 'L' | 'M' | 'Q' | 'H' = 'M') {
  const d = new TextEncoder().encode(data);
  const storeLen = d.length + 3;
  const eccByte = { L: 48, M: 49, Q: 50, H: 51 }[ecc];
  return concat(
    new Uint8Array([0x1D, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]),            // model 2
    new Uint8Array([0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, Math.max(1, Math.min(16, moduleSize))]),
    new Uint8Array([0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, eccByte]),
    new Uint8Array([0x1D, 0x28, 0x6B, storeLen & 0xFF, (storeLen >> 8) & 0xFF, 0x31, 0x50, 0x30]),
    d,
    new Uint8Array([0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30]),                  // print
  );
}

export type QrMethod = 'escpos' | 'dc3';
export function cmdQr(method: QrMethod, data: string, moduleSize: number) {
  return method === 'escpos' ? cmdQrcodeEscPos(data, Math.min(moduleSize, 8)) : cmdQrcode(data, moduleSize);
}

/**
 * GS v 0 raster — `widthBytes` × `height`, 1 = black, MSB first. Prints on "IP1000 Printer"
 * firmware; prints NOTHING on "Masung Printer" firmware (field report, izi_kiosco).
 */
export function cmdRaster(widthBytes: number, height: number, bits: Uint8Array) {
  return concat(
    new Uint8Array([0x1D, 0x76, 0x30, 0x00, widthBytes & 0xFF, widthBytes >> 8, height & 0xFF, height >> 8]),
    bits,
  );
}
